import { Scissors, Trash2 } from 'lucide-react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { cn } from '@r/utils/utils.ts';

export type TreeFlowNodeData = {
  label: string;
  /** Petit badge optionnel sous le label (ex. nombre de subordonnés). */
  badge?: string;
  showTargetHandle: boolean;
  showSourceHandle: boolean;
  targetConnectable: boolean;
  sourceConnectable: boolean;
  /** Défaut Top/Bottom (arbre vertical) — passer Left/Right pour un arbre horizontal. */
  targetPosition?: Position;
  sourcePosition?: Position;
  /** Style mis en avant (ex. tête(s) d'un arbre) — même nœud, juste plus visible. */
  emphasized?: boolean;
  /** Coin haut-gauche au survol : supprime le lien entrant et le nœud du canvas. Absent = pas de lien à supprimer. */
  onTrash?: () => void;
  trashAriaLabel?: string;
  /** Coin haut-droit au survol : supprime le lien entrant, garde le nœud visible (orphelin). Absent = pas de lien à couper. */
  onCut?: () => void;
  cutAriaLabel?: string;
  /** Suppression en cours (n'importe laquelle des 2 actions) : désactive les deux boutons. */
  relationActionPending?: boolean;
};

export type TreeFlowNodeType = Node<TreeFlowNodeData, string>;

/**
 * Nœud carte générique pour un canvas React Flow en arbre : label + badge
 * optionnel, `Handle` conditionnels selon les flags fournis par le domaine
 * appelant, orientables (arbre vertical ou horizontal). Copié-adapté de
 * `custom-shop-categories-flow-node.tsx` (mutualisé, aucune logique métier
 * ici — `onTrash`/`onCut` sont de simples callbacks, la logique de
 * suppression/confirmation reste côté appelant).
 */
export function TreeFlowNode({ data }: NodeProps<TreeFlowNodeType>) {
  return (
    <div
      className={cn(
        'group/node relative min-w-[160px] rounded-lg border-2 bg-background px-4 py-2 text-center text-sm font-medium',
        data.emphasized ? 'border-primary shadow-lg' : 'border-border shadow-md'
      )}
    >
      {data.onTrash && (
        <button
          type="button"
          onClick={data.onTrash}
          title={data.trashAriaLabel}
          disabled={data.relationActionPending}
          className="absolute -left-2 -top-2 flex items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity nodrag nopan size-6 hover:text-destructive group-hover/node:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
      {data.onCut && (
        <button
          type="button"
          onClick={data.onCut}
          title={data.cutAriaLabel}
          disabled={data.relationActionPending}
          className="absolute -right-2 -top-2 flex items-center justify-center rounded-full border border-border bg-background text-muted-foreground opacity-0 shadow-sm transition-opacity nodrag nopan size-6 hover:text-foreground group-hover/node:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Scissors className="size-3.5" />
        </button>
      )}

      {data.showTargetHandle && (
        <Handle
          id="target"
          type="target"
          position={data.targetPosition ?? Position.Top}
          isConnectable={data.targetConnectable}
          className="!size-3 !border-border !bg-muted"
        />
      )}
      <p className="truncate">{data.label}</p>
      {data.badge && (
        <p className="text-xs font-normal text-muted-foreground truncate">{data.badge}</p>
      )}
      {data.showSourceHandle && (
        <Handle
          id="source"
          type="source"
          position={data.sourcePosition ?? Position.Bottom}
          isConnectable={data.sourceConnectable}
          className="!size-3 !border-border !bg-muted"
        />
      )}
    </div>
  );
}
